import { Server } from '#enums';
import { Dtos, GroupedSection } from '#models';
import { computed, Injectable, signal } from '@angular/core';

export function computePssrTitle(bu: Dtos.BusinessUnit | null | undefined): string {
  if (!bu) return 'PSSR';
  const parts: string[] = ['PSSR'];
  if (bu.ShowCCC) parts.push('CCC');
  if (bu.ShowORR) parts.push('ORR');
  return parts.join(' / ');
}

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  updateCurrentPssrSections(updateFn: (sections: GroupedSection[]) => GroupedSection[]) {
    this._currentPssrSections.update(updateFn);
  }
  togglePssrSection(section: GroupedSection) {
    this._currentPssrSections.update(sections =>
      sections.map(s =>
        s.ID === section.ID && s.Description === section.Description ? { ...s, isCollapsed: !s.isCollapsed } : s
      )
    );
  }
  private _currentPssrSections = signal<GroupedSection[]>([]);
  setCurrentPssrSections(updatedSections: GroupedSection[]) {
    this._currentPssrSections.set(updatedSections);
  }
  currentPssrSections = computed(() => {
    const result = this._currentPssrSections.asReadonly();
    const pssr = this._currentPssr();

    if (!pssr) {
      return [];
    }

    return result().filter(section => this._isVisibleSection(section, pssr));
  });

  private _isBlankSection = (section: GroupedSection) => {
    return section.Type === Server.SectionType.StandAlone
      ? section.Responses.flatMap(r => r.ActionItems).length === 0
      : section.Responses.length === 0;
  };

  private _isVisibleSection = (section: GroupedSection, pssr: Dtos.PSSR) => {
    switch (section.Type) {
    case Server.SectionType.Body:
      return !this._isBlankSection(section);
    case Server.SectionType.ProjectSpecific:
      return (
        !this._isBlankSection(section) ||
          ([Server.PSSRStatus.Draft, Server.PSSRStatus.Initiated].includes(pssr.Status) && pssr.CurrentUser.CanEdit)
      );
    case Server.SectionType.StandAlone: {
      if (pssr.Status >= Server.PSSRStatus.Approved && this._isBlankSection(section)) {
        return false;
      }

      if (pssr.Status < Server.PSSRStatus.Completed) {
        return false;
      }

      return true;
    }
    case Server.SectionType.Signature: {
      return pssr.Status !== Server.PSSRStatus.Draft && !this._isBlankSection(section);
    }
    }
  };

  // this._currentPssrSections.asReadonly();

  isSectionLead = (sectionId = 0, sectionDescription = '') =>
    computed(() => {
      const currentUser = this.currentPssr()?.CurrentUser;
      const businessUnitCode = this.currentBu()?.Code;

      const pssrSectionUsers = this.currentPssrSections().flatMap(section => section.sectionUsers);

      return pssrSectionUsers.some(
        sectionUser =>
          (sectionUser.SectionDescription == sectionDescription || sectionUser.SectionID == sectionId) &&
          (sectionUser.UserID === currentUser?.UserID ||
            currentUser?.ParentUsers.includes(sectionUser.UserEmail) ||
            (businessUnitCode === Server.BusinessUnitCode.TCO &&
              currentUser?.ParentUsers.some(parentUser => sectionUser.ParentUsers.includes(parentUser))))
      );
    });

  deleteCurrentPssrReponse(arg0: Dtos.PSSRResponse) {
    this._currentPssrResponses.update(responses => responses.filter(r => r.Id !== arg0.Id));
    this._currentPssrSections.update(sections =>
      sections.map(s => ({
        ...s,
        SubSections: s.SubSections.map(sub => ({
          ...sub,
          Responses: sub.Responses.filter(r => r.Id !== arg0.Id),
        })),
      }))
    );
  }
  updateCurrentPssrResponse(updatedResponse: Dtos.PSSRResponse): void {
    this._currentPssrResponses.update(responses =>
      responses.map(r => (r.Id === updatedResponse.Id ? updatedResponse : r))
    );
    this._currentPssrSections.update(sections =>
      sections.map(s => ({
        ...s,
        SubSections: s.SubSections.map(sub => ({
          ...sub,
          Responses: sub.Responses.map(r => (r.Id === updatedResponse.Id ? updatedResponse : r)),
        })),
      }))
    );
  }
  allowMembersToCompletePSSR = computed(() => {
    const bu = this._currentBu();
    const pssr = this._currentPssr();

    if (
      bu &&
      bu.AllowMembersToCompletePSSR &&
      pssr &&
      (pssr.Status === Server.PSSRStatus.InProgress || pssr.Status === Server.PSSRStatus.Initiated)
    ) {
      return (
        pssr.PSSRUsers.Users.some(u => u.UserID === pssr.CurrentUser.UserID) ||
        pssr.CurrentUser.Role !== Server.UserRole.None
      );
    }
    return null;
  });
  setCurrentPssr(pssr: Dtos.PSSR | null) {
    this._currentPssr.set(pssr);
  }
  setCurrentPssrUsers(pssrUsers: Dtos.PSSRUser[]) {
    this._currentPssr.update(pssr =>
      pssr
        ? {
          ...pssr,
          PSSRUsers: {
            ResultModel: pssr.PSSRUsers.ResultModel,
            Users: pssrUsers,
          },
        }
        : null
    );
  }

  updateCurrentPssr(updateFn: (currentPssr: Dtos.PSSR | null) => Dtos.PSSR | null) {
    this._currentPssr.update(updateFn);
  }

  addPssrUser(user: Dtos.PSSRUser) {
    this._currentPssr.update(pssr =>
      pssr
        ? {
          ...pssr,
          PSSRUsers: {
            ...pssr.PSSRUsers,
            Users: [...(pssr.PSSRUsers?.Users ?? []), user],
          },
        }
        : null
    );
  }

  setCurrentBu(firstBu: Dtos.BusinessUnit | null) {
    this._currentBu.set(firstBu ? { ...firstBu, PssrTitle: computePssrTitle(firstBu) } : null);
  }
  private _currentPssr = signal<Dtos.PSSR | null>(null);
  currentPssr = computed(() => {
    const pssr = this._currentPssr();

    if (!pssr) {
      return null;
    }

    const nextStatus = this.getCurrentPssrNextStatus();

    return {
      ...pssr,
      NextStatusId: nextStatus?.NextStatusId ?? pssr?.NextStatusId,
      NextStatus: nextStatus?.NextStatus ?? pssr?.NextStatus,
    };
  });

  currentPssrTitle = computed(() => computePssrTitle(this._currentBu()));

  currentFinalApproverPssr = computed(() => {
    const pssr = this._currentPssr();
    return (
      pssr?.FinalApprover ?? pssr?.PSSRUsers?.Users?.find(u => u.Role === Server.UserRole.Approver)?.UserName ?? null
    );
  });

  private _currentBu = signal<Dtos.BusinessUnit | null>(null);
  currentBu = this._currentBu.asReadonly();

  private _currentTaskPlan = signal<Dtos.TaskPlan | null>(null);
  setCurrentTaskPlan(taskPlan: Dtos.TaskPlan | null) {
    this._currentTaskPlan.set(taskPlan);
  }

  private _currentPssrStandAloneItems = signal<Dtos.ResponseItem[]>([]);
  currentPssrStandAloneItems = this._currentPssrStandAloneItems.asReadonly();
  setCurrentPssrStandAloneItems(items: Dtos.ResponseItem[]) {
    this._currentPssrStandAloneItems.set(items);
  }

  private _currentPssrResponses = signal<Dtos.PSSRResponse[]>([]);
  currentPssrResponses = this._currentPssrResponses.asReadonly();
  setCurrentPssrResponses(responses: Dtos.PSSRResponse[]) {
    this._currentPssrResponses.set(responses);
  }

  private getCurrentPssrNextStatus = computed(() => {
    const pssr = this._currentPssr();
    if (!pssr) {
      return null;
    }

    const sections = this._currentPssrSections();

    const actionItems = sections
      .flatMap(section => section.SubSections ?? [])
      .flatMap(subsection => subsection.Responses ?? [])
      .flatMap(response => response.ActionItems ?? []);

    const hasP3RejectedActions = actionItems.some(
      item => item.Priority === Server.ResponseItemPriority.P3 && item.Status === Server.ResponseItemStatus.Rejected
    );

    if (
      pssr.Status === Server.PSSRStatus.PendingApproval &&
      hasP3RejectedActions &&
      pssr.CurrentUser.Role == Server.UserRole.Approver
    ) {
      return {
        NextStatus: 'pssrDetails.actions.returnToTeamLead',
        NextStatusId: Server.PSSRStatus.Completed,
      };
    }

    return {
      NextStatus: pssr.NextStatus,
      NextStatusId: pssr.NextStatusId,
    };
  });

  updatePostWalkdownActionItem(parentSection: GroupedSection, item: Dtos.ResponseItem) {
    this._currentPssrSections.update(sections =>
      sections.map(s =>
        s.ID === parentSection.ID && s.Description === parentSection.Description
          ? {
            ...parentSection,
            SubSections: parentSection.SubSections.map(sub => ({
              ...sub,
              Responses: sub.Responses.map(r =>
                r.Id === item.ResponseID
                  ? {
                    ...r,
                    ActionItems: r.ActionItems.map(i => (i.Id === item.Id ? item : i)),
                  }
                  : r
              ),
            })),
          }
          : s
      )
    );
  }

  deletePostWalkdownActionItem(parentSection: GroupedSection, postWalkdownActionItemId: number) {
    this._currentPssrSections.update(sections =>
      sections.map(s =>
        s.ID === parentSection.ID && s.Description === parentSection.Description
          ? {
            ...parentSection,
            SubSections: parentSection.SubSections.map(sub => ({
              ...sub,
              Responses: sub.Responses.map(r =>
                !r.Id
                  ? {
                    ...r,
                    ActionItems: r.ActionItems.filter(i => i.Id !== postWalkdownActionItemId),
                  }
                  : r
              ),
            })),
          }
          : s
      )
    );
  }

  addPostWalkdownActionItem(parentSection: GroupedSection, item: Dtos.ResponseItem) {
    this._currentPssrSections.update(sections =>
      sections.map(s =>
        s.ID === parentSection.ID && s.Description === parentSection.Description
          ? {
            ...parentSection,
            SubSections: parentSection.SubSections.map(sub => ({
              ...sub,
              Responses: sub.Responses.map(r =>
                !r.Id
                  ? {
                    ...r,
                    ActionItems: [...r.ActionItems, item],
                  }
                  : r
              ),
            })),
          }
          : s
      )
    );
  }
}
